import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {
  defaultSchedule,
  loadSchedule,
  saveSchedule,
  resetSchedule,
  iconOptions,
  typeOptions,
} from '../data/schedule';

const PASSWORD = 'inTouch';
const SESSION_KEY = 'intouch_admin_auth';

const typeColors = {
  logistics: 'bg-cyan-400',
  keynote: 'bg-primary',
  break: 'bg-amber-400',
  session: 'bg-purple-500',
  workshop: 'bg-emerald-500',
  social: 'bg-rose-500',
};

const typeBadgeColors = {
  logistics: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20',
  keynote: 'bg-primary/10 text-primary border-primary/20',
  break: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  session: 'bg-purple-500/10 text-purple-700 border-purple-500/20',
  workshop: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20',
  social: 'bg-rose-500/10 text-rose-700 border-rose-500/20',
};

const genId = () => `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const inputCls =
  'w-full bg-white border border-slate-200 focus:border-primary rounded-lg px-3 py-2 text-slate-800 text-sm focus:outline-none transition-colors';

// ─── Password Gate ────────────────────────────────────────────────────────────
const PasswordGate = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, 'true');
      onSuccess();
    } else {
      setError(true);
      setShake(true);
      setPassword('');
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <motion.div
          animate={shake ? { x: [-8, 8, -6, 6, -4, 0] } : {}}
          transition={{ duration: 0.4 }}
          className="glass-card rounded-2xl p-8"
        >
          <div className="text-center mb-7">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 mb-4">
              <SafeIcon icon={FiIcons.FiLock} className="text-primary text-2xl" />
            </div>
            <h1 className="text-2xl font-display font-bold text-slate-900 mb-1">Admin Access</h1>
            <p className="text-slate-500 text-sm">Enter the password to edit the program.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(false);
                }}
                placeholder="Password"
                className={`w-full bg-white border rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary transition-colors ${
                  error ? 'border-rose-400' : 'border-slate-200'
                }`}
                autoFocus
              />
              <AnimatePresence>
                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-rose-500 text-xs mt-1.5 flex items-center gap-1"
                  >
                    <SafeIcon icon={FiIcons.FiAlertCircle} className="text-sm" />
                    Incorrect password. Try again.
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
            <button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 active:bg-primary text-white font-semibold rounded-xl px-4 py-3 transition-colors flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiIcons.FiUnlock} className="text-base" />
              Unlock
            </button>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
};

// ─── Edit Form ────────────────────────────────────────────────────────────────
const EditForm = ({ form, onChange, onSave, onCancel }) => (
  <div className="glass-card rounded-2xl p-5 border border-primary/20">
    <div className="grid grid-cols-2 gap-3 mb-3">
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1.5">Time</label>
        <input
          type="text"
          value={form.time}
          onChange={(e) => onChange({ time: e.target.value })}
          placeholder="09:00"
          className={inputCls}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1.5">Type</label>
        <select
          value={form.type}
          onChange={(e) => onChange({ type: e.target.value })}
          className={inputCls}
        >
          {typeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className="mb-3">
      <label className="block text-xs font-medium text-slate-500 mb-1.5">Title</label>
      <input
        type="text"
        value={form.title}
        onChange={(e) => onChange({ title: e.target.value })}
        className={inputCls}
      />
    </div>

    <div className="mb-3">
      <label className="block text-xs font-medium text-slate-500 mb-1.5">Description</label>
      <textarea
        value={form.description}
        onChange={(e) => onChange({ description: e.target.value })}
        rows={2}
        className={`${inputCls} resize-none`}
      />
    </div>

    <div className="mb-5">
      <label className="block text-xs font-medium text-slate-500 mb-1.5">Icon</label>
      <div className="flex items-center gap-3">
        <select
          value={form.icon}
          onChange={(e) => onChange({ icon: e.target.value })}
          className={`${inputCls} flex-1`}
        >
          {iconOptions.map((ico) => (
            <option key={ico} value={ico}>
              {ico}
            </option>
          ))}
        </select>
        <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20 text-primary shrink-0">
          <SafeIcon name={form.icon} className="text-xl" />
        </div>
      </div>
    </div>

    <div className="flex items-center gap-2">
      <button
        onClick={onSave}
        className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors"
      >
        <SafeIcon icon={FiIcons.FiCheck} className="text-base" />
        Done
      </button>
      <button
        onClick={onCancel}
        className="px-3 py-2 text-slate-400 hover:text-slate-700 text-sm rounded-lg transition-colors"
      >
        Cancel
      </button>
    </div>
  </div>
);

// ─── Schedule Row ─────────────────────────────────────────────────────────────
const ScheduleRow = ({ item, index, total, onEdit, onDelete, onMove }) => (
  <div className="glass-card rounded-xl px-4 py-3.5 flex items-center gap-3 group">
    {/* Reorder arrows */}
    <div className="flex flex-col gap-0.5 shrink-0">
      <button
        onClick={() => onMove(index, -1)}
        disabled={index === 0}
        className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
        title="Move up"
      >
        <SafeIcon icon={FiIcons.FiChevronUp} className="text-sm" />
      </button>
      <button
        onClick={() => onMove(index, 1)}
        disabled={index === total - 1}
        className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
        title="Move down"
      >
        <SafeIcon icon={FiIcons.FiChevronDown} className="text-sm" />
      </button>
    </div>

    {/* Type colour dot */}
    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${typeColors[item.type] || 'bg-primary'}`} />

    {/* Icon */}
    <div className="p-1.5 rounded-lg bg-slate-100 text-slate-500 shrink-0">
      <SafeIcon name={item.icon} className="text-base" />
    </div>

    {/* Content */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-0.5">
        <span className="text-primary font-mono text-xs font-bold">{item.time}</span>
        <span
          className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
            typeBadgeColors[item.type] || 'bg-primary/10 text-primary border-primary/20'
          }`}
        >
          {item.type}
        </span>
      </div>
      <p className="text-slate-900 text-sm font-medium truncate">{item.title}</p>
      <p className="text-slate-500 text-xs truncate">{item.description}</p>
    </div>

    {/* Action buttons */}
    <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        onClick={() => onEdit(item)}
        className="p-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
        title="Edit"
      >
        <SafeIcon icon={FiIcons.FiEdit2} className="text-sm" />
      </button>
      <button
        onClick={() => onDelete(item.id)}
        className="p-2 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
        title="Delete"
      >
        <SafeIcon icon={FiIcons.FiTrash2} className="text-sm" />
      </button>
    </div>
  </div>
);

// ─── Admin Editor ─────────────────────────────────────────────────────────────
const AdminEditor = ({ onLogout }) => {
  const [schedule, setSchedule] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saveState, setSaveState] = useState('idle'); // idle | saved | error
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    setSchedule(loadSchedule());
  }, []);

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ ...item });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = () => {
    setSchedule((prev) =>
      prev.map((item) => (item.id === editingId ? { ...editForm } : item))
    );
    setEditingId(null);
    setEditForm({});
  };

  const deleteItem = (id) => {
    if (editingId === id) cancelEdit();
    setSchedule((prev) => prev.filter((item) => item.id !== id));
  };

  const moveItem = (index, direction) => {
    const next = [...schedule];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setSchedule(next);
  };

  const addItem = () => {
    const newItem = {
      id: genId(),
      time: '12:00',
      title: 'New Event',
      description: 'Add a description here.',
      icon: 'Calendar',
      type: 'session',
    };
    setSchedule((prev) => [...prev, newItem]);
    setEditingId(newItem.id);
    setEditForm({ ...newItem });
  };

  const handleSave = () => {
    saveSchedule(schedule);
    setSaveState('saved');
    setTimeout(() => setSaveState('idle'), 2500);
  };

  const handleReset = () => {
    if (confirmReset) {
      resetSchedule();
      setSchedule(defaultSchedule);
      setEditingId(null);
      setEditForm({});
      setConfirmReset(false);
    } else {
      setConfirmReset(true);
      setTimeout(() => setConfirmReset(false), 3000);
    }
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-28 pb-16 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-3 py-1 mb-2">
              <SafeIcon icon={FiIcons.FiSettings} className="text-primary text-xs" />
              <span className="text-xs font-medium text-primary">Admin Panel</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">Program Editor</h1>
            <p className="text-slate-500 text-sm mt-1">
              Edit, reorder, add or remove schedule items. Changes save to your browser.
            </p>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 text-sm transition-colors shrink-0 mt-1"
            title="Sign out"
          >
            <SafeIcon icon={FiIcons.FiLogOut} className="text-base" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all ${
              saveState === 'saved'
                ? 'bg-emerald-600 text-white'
                : 'bg-primary hover:bg-primary/90 text-white'
            }`}
          >
            <SafeIcon
              icon={saveState === 'saved' ? FiIcons.FiCheck : FiIcons.FiSave}
              className="text-base"
            />
            {saveState === 'saved' ? 'Saved!' : 'Save Changes'}
          </button>

          <button
            onClick={addItem}
            className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors"
          >
            <SafeIcon icon={FiIcons.FiPlus} className="text-base" />
            Add Item
          </button>

          <button
            onClick={handleReset}
            className={`ml-auto flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              confirmReset
                ? 'text-rose-500 bg-rose-50 border border-rose-200'
                : 'text-slate-400 hover:text-slate-700'
            }`}
          >
            <SafeIcon icon={FiIcons.FiRotateCcw} className="text-base" />
            <span>{confirmReset ? 'Click again to confirm' : 'Reset to Default'}</span>
          </button>
        </div>

        {/* Schedule list */}
        <div className="space-y-3">
          <AnimatePresence initial={false}>
            {schedule.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -24, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.2 }}
              >
                {editingId === item.id ? (
                  <EditForm
                    form={editForm}
                    onChange={(patch) => setEditForm((f) => ({ ...f, ...patch }))}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                  />
                ) : (
                  <ScheduleRow
                    item={item}
                    index={index}
                    total={schedule.length}
                    onEdit={startEdit}
                    onDelete={deleteItem}
                    onMove={moveItem}
                  />
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {schedule.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <SafeIcon
                icon={FiIcons.FiCalendar}
                className="text-4xl mx-auto mb-3 opacity-30"
              />
              <p className="text-sm">No schedule items yet.</p>
            </div>
          )}
        </div>

        {/* Bottom add button */}
        {schedule.length > 0 && (
          <button
            onClick={addItem}
            className="mt-4 w-full py-3 rounded-xl border border-dashed border-slate-300 hover:border-primary/50 text-slate-400 hover:text-primary text-sm flex items-center justify-center gap-2 transition-colors"
          >
            <SafeIcon icon={FiIcons.FiPlus} className="text-base" />
            Add Item
          </button>
        )}

        <p className="text-center text-slate-400 text-xs mt-8">
          Navigate to <span className="text-slate-500 font-mono">/#/program</span> to preview your changes.
        </p>
      </div>
    </div>
  );
};

// ─── Admin Page ───────────────────────────────────────────────────────────────
const Admin = () => {
  const [authenticated, setAuthenticated] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === 'true'
  );

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthenticated(false);
  };

  if (!authenticated) {
    return <PasswordGate onSuccess={() => setAuthenticated(true)} />;
  }

  return <AdminEditor onLogout={handleLogout} />;
};

export default Admin;
